import React, { useState, useEffect } from 'react';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddLinkIcon from '@mui/icons-material/AddLinkRounded';
import LinkOffIcon from '@mui/icons-material/LinkOffRounded';
import ReplayIcon from '@mui/icons-material/ReplayRounded';
import HealthIcon from '@mui/icons-material/MonitorHeartRounded';
import { useSelector } from 'react-redux'
import endpoints, { apiRequest } from '../../../endpoints';
import Confirmation from '../modals/Confirmation';

let authWindow = null
let authWindowInterval = null

function expiryLabel(isoDate) {
    if (!isoDate) {
        return 'unknown'
    }

    const diffMinutes = Math.round((new Date(isoDate).getTime() - Date.now()) / 60000)
    if (diffMinutes <= 0) {
        return 'expired'
    }
    if (diffMinutes < 60) {
        return 'in ' + diffMinutes + ' min'
    }
    if (diffMinutes < 60 * 24) {
        return 'in ' + Math.round(diffMinutes / 60) + ' h'
    }
    return 'in ' + Math.round(diffMinutes / (60 * 24)) + ' d'
}

function accountName(account) {
    return account.name || account.email || ('Upwork account #' + account.id)
}

export default function UpworkIntegration(props) {
    const [state, setState] = useState(null)
    const [loading, setLoading] = useState(false)
    const [busyId, setBusyId] = useState(null)
    const [disconnect, setDisconnect] = useState(null)

    const token = useSelector((state) => state.auth.token)

    const showAlertError = (message) => {
        props.showAlert(message, 'error')
    }

    const showAlertSuccess = (message) => {
        props.showAlert(message, 'success')
    }

    const load = async () => {
        setLoading(true)
        try {
            const res = await apiRequest('get', endpoints.integrations_upwork, {
                token: token,
            })
            if (res.data.status === 'success') {
                setState(res.data)
            } else {
                showAlertError(res.data.message || 'Unknown Error - get Upwork integration status')
            }
        } catch (e) {
            showAlertError('Unknown Error - get Upwork integration status')
        }
        setLoading(false)
    }

    const connectHandle = async () => {
        try {
            const res = await apiRequest('get', endpoints.integrations_upwork_login, {
                token: token,
            })

            if (!res.data.oauthUrl) {
                showAlertError(res.data.message || 'Unknown Error - Upwork connect')
                return
            }

            const left = (window.screen.width - 600) / 2
            const top = (window.screen.height - 750) / 2
            authWindow = window.open(res.data.oauthUrl, 'upworkAuthConnect',
                'left=' + left + ',top=' + top + ',width=600,height=750')
        } catch (e) {
            showAlertError('Unknown Error - Upwork connect')
        }
    }

    const refreshHandle = async (account) => {
        setBusyId(account.id)
        try {
            const res = await apiRequest('post', endpoints.integrations_upwork_refresh, {
                token: token,
                account_id: account.id,
            })
            if (res.data.status === 'success') {
                showAlertSuccess('Token refreshed, valid ' + expiryLabel(res.data.expires_at))
            } else {
                showAlertError(res.data.message || 'Token refresh failed')
            }
        } catch (e) {
            showAlertError(e.response?.data?.message || 'Token refresh failed')
        }
        setBusyId(null)
        load()
    }

    const checkHandle = async (account) => {
        setBusyId(account.id)
        try {
            const res = await apiRequest('post', endpoints.integrations_upwork_check, {
                token: token,
                account_id: account.id,
            })
            if (res.data.status === 'success' && res.data.active) {
                const profile = res.data.upwork_profile
                showAlertSuccess('Connection works - Upwork account: '
                    + [profile?.name, profile?.email].filter(Boolean).join(', '))
            } else {
                showAlertError(res.data.message || 'The Upwork token is not usable')
            }
        } catch (e) {
            showAlertError(e.response?.data?.message || 'The Upwork token is not usable')
        }
        setBusyId(null)
        load()
    }

    const serviceAccountHandle = async (accountId) => {
        setBusyId(accountId)
        try {
            const res = await apiRequest('put', endpoints.integrations_upwork_service_account, {
                token: token,
                account_id: accountId,
            })
            if (res.data.status === 'success') {
                showAlertSuccess('Data saved successfully')
            } else {
                showAlertError(res.data.message || 'Unknown Error - set account')
            }
        } catch (e) {
            showAlertError(e.response?.data?.message || 'Unknown Error - set account')
        }
        setBusyId(null)
        load()
    }

    const disconnectHandle = async (response) => {
        const account = disconnect
        setDisconnect(null)

        if (!response || !account) {
            return
        }

        setBusyId(account.id)
        try {
            const res = await apiRequest('delete', endpoints.integrations_upwork_disconnect, {
                token: token,
                id: account.id,
            })
            if (res.data.status === 'success') {
                showAlertSuccess('Upwork account disconnected')
            } else {
                showAlertError(res.data.message || 'Unknown Error - disconnect')
            }
        } catch (e) {
            showAlertError('Unknown Error - disconnect')
        }
        setBusyId(null)
        load()
    }

    useEffect(() => {
        load()

        // The OAuth popup closes itself once the backend stored the tokens.
        if (authWindowInterval) {
            clearInterval(authWindowInterval)
        }
        authWindowInterval = setInterval(() => {
            if (authWindow && authWindow.closed) {
                authWindow = null
                load()
            }
        }, 1000)

        return () => {
            clearInterval(authWindowInterval)
            authWindowInterval = null
        }
    }, [])

    const accounts = state?.items ?? []

    return (
        <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={2}>
                <Box>
                    <Typography variant="subtitle1">Upwork</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Upwork accounts the app talks to the Upwork API as. A token
                        belongs to the Upwork account that authorised it, independently
                        of who is signed in to Gerda.
                    </Typography>
                </Box>
                <Stack sx={{flexGrow: 1}} />
                {loading ? <CircularProgress size={20} /> : null}
                {state?.service_account_source === 'configured' ?
                <Button
                    variant="outlined"
                    onClick={() => {serviceAccountHandle('')}}
                >
                    Unpin Account
                </Button> : null}
                <Button
                    variant="outlined"
                    startIcon={<ReplayIcon />}
                    onClick={load}
                >
                    Reload
                </Button>
                <Button
                    variant="contained"
                    color="success"
                    startIcon={<AddLinkIcon />}
                    onClick={connectHandle}
                    disabled={!state?.configured}
                >
                    Connect Account
                </Button>
            </Stack>

            {state && !state.configured ?
            <Alert severity="error">
                UPWORK_CLIENT_ID / UPWORK_CLIENT_SECRET are not set in the backend
                environment, so no Upwork account can be connected.
            </Alert> : null}

            {state?.configured && !accounts.length ?
            <Alert severity="warning">
                No Upwork account is connected. Job feeds cannot be loaded until one is.
            </Alert> : null}

            {state?.configured && accounts.length && state.service_account_source === 'auto' ?
            <Alert severity="info">
                No account is pinned, so the most recently connected one is used for
                every Upwork call. Pick an account below to make that choice explicit.
            </Alert> : null}

            {/*
              * Only an explicit pin checks the radio. Showing the auto-resolved
              * account as checked would make it unpinnable: clicking an already
              * checked radio fires no change event.
              */}
            <RadioGroup
                value={state?.service_account_source === 'configured' ? state.service_account_id : ''}
                onChange={(e) => {serviceAccountHandle(e.target.value)}}
            >
                <Stack spacing={1.5}>
                    {accounts.map((account) => (
                        <Paper key={account.id} variant="outlined" sx={{p: 2}}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar src={account.photo_url} alt="">
                                    {accountName(account).substr(0, 1)}
                                </Avatar>
                                <Stack sx={{flexGrow: 1, minWidth: 0}}>
                                    <Typography variant="body1" noWrap>
                                        {accountName(account)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        {[account.email, account.upwork_nid ? 'nid ' + account.upwork_nid : null]
                                            .filter(Boolean).join(' \u00b7 ')}
                                    </Typography>
                                    <Stack direction="row" spacing={1} sx={{mt: 1}} flexWrap="wrap" useFlexGap>
                                        {account.access_token_valid ?
                                        <Chip
                                            size="small"
                                            color="success"
                                            label={'Access token expires ' + expiryLabel(account.expires_at)}
                                        />
                                        :
                                        <Chip
                                            size="small"
                                            color="warning"
                                            label="Access token expired - refreshed on next use"
                                        />}
                                        {account.has_refresh_token ? null :
                                        <Chip size="small" color="error" label="No refresh token" />}
                                        {account.upwork_nid ? null :
                                        <Tooltip title="Imported from the old per-user storage - run Check to identify it">
                                            <Chip size="small" color="warning" variant="outlined" label="Unidentified account" />
                                        </Tooltip>}
                                        {account.needs_keepalive ?
                                        <Tooltip title={'The refresh token has not been rotated for over '
                                            + (state?.keepalive_days ?? 7) + ' days'}>
                                            <Chip size="small" color="warning" variant="outlined" label="Needs keep-alive" />
                                        </Tooltip> : null}
                                        {account.is_service_account ?
                                        <Chip
                                            size="small"
                                            color="primary"
                                            variant={state?.service_account_source === 'configured' ? 'filled' : 'outlined'}
                                            label={state?.service_account_source === 'configured'
                                                ? 'Used by the app'
                                                : 'Used by the app (auto)'}
                                        /> : null}
                                    </Stack>
                                </Stack>
                                <Stack spacing={1} alignItems="flex-end">
                                    <FormControlLabel
                                        sx={{mr: 0}}
                                        control={<Radio value={account.id} size="small" />}
                                        label={<Typography variant="body2">Use for Upwork API calls</Typography>}
                                        disabled={busyId === account.id}
                                    />
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            size="small"
                                            startIcon={<HealthIcon />}
                                            onClick={() => {checkHandle(account)}}
                                            disabled={busyId === account.id}
                                        >
                                            Check
                                        </Button>
                                        <Button
                                            size="small"
                                            startIcon={<ReplayIcon />}
                                            onClick={() => {refreshHandle(account)}}
                                            disabled={busyId === account.id}
                                        >
                                            Refresh
                                        </Button>
                                        <Button
                                            size="small"
                                            color="error"
                                            startIcon={<LinkOffIcon />}
                                            onClick={() => {setDisconnect(account)}}
                                            disabled={busyId === account.id}
                                        >
                                            Disconnect
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            </RadioGroup>

            {state?.redirect_uri ?
            <React.Fragment>
                <Divider />
                <Typography variant="caption" color="text.secondary">
                    The Upwork app must allow this exact redirect URI: {state.redirect_uri}
                    {state?.keepalive_days ?
                    <React.Fragment>
                        {' '}&middot; a scheduled job rotates the tokens every {state.keepalive_days} days,
                        and every API call refreshes an expired access token automatically.
                    </React.Fragment> : null}
                </Typography>
            </React.Fragment> : null}

            <Confirmation
                data={disconnect}
                title="Disconnect Upwork Account"
                content={disconnect
                    ? 'Remove the stored Upwork tokens of ' + accountName(disconnect) + '?'
                    : ''}
                onResponse={disconnectHandle}
            />
        </Stack>
    )
}
